import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import { toast } from 'react-toastify'
import { connect } from 'react-redux'
import clsx from 'clsx'

import http from '../../../../shared/http'
import { TApi, TRoutes } from '../../../../shared/const'
import { errorText } from '../../../../shared/errorMessage'
import { logIn } from '../../../../redux/ducks/user'

import Slide from './Slide'
import CompareSlide from './CompareSlide'
import Quiz from './Quiz'
import Licence from './Licence'
import { JOKER_BUDGET, SLIDES, TOTAL_QUESTIONS } from './content'
import styles from './style.module.scss'

/*
 * Die Schulung „Neu im Schuljahr 2026/27".
 *
 * Eine Präsentation im Format 16:9, durch die man sich mit den Pfeiltasten
 * klickt. Zwischen den Themen stehen kurze Fragen; wer danebenliegt, versucht
 * es noch einmal. Am Ende steht eine Abschlusskarte im eigenen Profil.
 *
 * WARUM NICHT ALS PDF ODER SLIDES-DECK
 * ------------------------------------
 * Weil beides am Tag der nächsten Auslieferung falsch ist und es niemandem
 * auffällt. Was hier steht, liegt im selben Repository wie die Anwendung,
 * verlinkt in dieselbe Anwendung und lässt sich mit ihr zusammen ändern.
 *
 * WIE DER STAND GESPEICHERT WIRD
 * ------------------------------
 * Nach jeder beantworteten Frage und jeder erfüllten Aufgabe geht der **ganze**
 * Stand an den Server (`PUT /admin/training`). Bewusst nicht einzelne
 * Ereignisse: Ein verlorener Aufruf — Netz weg, Tab zu — risse sonst ein Loch
 * in die Zählung, das niemand mehr findet. Gerechnet wird am Server; die
 * Oberfläche meldet nur, was passiert ist.
 *
 * Gespeichert wird **entprellt**: Wer sich schnell durchklickt, löst sonst ein
 * halbes Dutzend Aufrufe aus, die einander überholen können.
 */

const SPEICHER_VERZUG = 900

function Training(props) {
	const [index, setIndex] = useState(0)
	const [stand, setStand] = useState(null)
	const [antworten, setAntworten] = useState({})
	const [aufgaben, setAufgaben] = useState([])
	const [jokerBenutzt, setJokerBenutzt] = useState({})
	const [laedt, setLaedt] = useState(true)
	const [pruefeAufgabe, setPruefeAufgabe] = useState(false)

	const rahmenRef = useRef(null)
	const speicherTimer = useRef(null)
	// Für den Speicheraufruf: der jeweils jüngste Stand, ohne den Effekt neu zu
	// binden. Ohne diese Referenz schickte der Timer den Stand von vorhin.
	const aktuell = useRef({ antworten: {}, aufgaben: [], index: 0 })
	aktuell.current = { antworten, aufgaben, index }

	const jokerGesamt = useMemo(() => Object.values(jokerBenutzt).reduce((summe, n) => summe + n, 0), [jokerBenutzt])
	const jokerLeft = Math.max(0, JOKER_BUDGET - jokerGesamt)

	const beantwortet = Object.keys(antworten).length
	const richtig = Object.values(antworten).filter(a => a.correct).length
	const durch = beantwortet >= TOTAL_QUESTIONS

	/* ------------------------------------------------------- Stand vom Server */

	useEffect(() => {
		http.get(TApi.ADMIN_TRAINING, { disableToast: true })
			.then(({ data }) => {
				setStand(data)
				setAntworten(data?.state?.answers || {})
				setAufgaben(data?.state?.tasks || [])
				// Fortsetzen, wo man aufgehört hat — aber nie auf der Schlussfolie
				// landen, wenn noch Fragen offen sind.
				const gemerkt = Number(data?.state?.slide) || 0
				setIndex(Math.min(Math.max(gemerkt, 0), SLIDES.length - 1))
				const jokers = Object.fromEntries(
					Object.entries(data?.state?.answers || {}).map(([key, wert]) => [key, wert.jokers || 0])
				)
				setJokerBenutzt(jokers)
			})
			.catch(error => toast.error(errorText(error, 'Der Onboarding-Stand konnte nicht geladen werden.')))
			.finally(() => setLaedt(false))
	}, [])

	const sichere = useCallback((sofort = false, completed = false) => {
		if (speicherTimer.current) clearTimeout(speicherTimer.current)

		const schicke = () =>
			http
				.put(
					TApi.ADMIN_TRAINING,
					{
						answers: aktuell.current.antworten,
						tasks: aktuell.current.aufgaben,
						slide: aktuell.current.index,
						// Die Zahl der Fragen **im Onboarding**. Ohne sie misst der
						// Server am beantworteten Teil, und eine einzige richtige
						// Antwort wäre eine glatte Eins.
						total: TOTAL_QUESTIONS,
						completed
					},
					{ disableToast: true }
				)
				.then(({ data }) => setStand(data))
				.catch(error => toast.error(errorText(error, 'Der Stand konnte nicht gesichert werden.')))

		if (sofort) return schicke()
		speicherTimer.current = setTimeout(schicke, SPEICHER_VERZUG)
		return undefined
	}, [])

	// Beim Verlassen der Seite noch schnell sichern — sonst fehlt die letzte
	// Antwort, ausgerechnet die, nach der jemand weggeklickt hat.
	useEffect(() => () => speicherTimer.current && clearTimeout(speicherTimer.current), [])

	/* --------------------------------------------------------- Navigation */

	const folie = SLIDES[index]
	const istFrage = folie && !folie.kind

	const weiter = useCallback(() => setIndex(i => Math.min(i + 1, SLIDES.length - 1)), [])
	const zurueck = useCallback(() => setIndex(i => Math.max(i - 1, 0)), [])

	useEffect(() => {
		const taste = ereignis => {
			// In einem Eingabefeld gehören die Pfeiltasten dem Feld.
			const ziel = ereignis.target
			if (ziel && (ziel.tagName === 'INPUT' || ziel.tagName === 'TEXTAREA')) return

			if (ereignis.key === 'ArrowRight' || ereignis.key === 'PageDown') weiter()
			if (ereignis.key === 'ArrowLeft' || ereignis.key === 'PageUp') zurueck()
		}

		window.addEventListener('keydown', taste)
		return () => window.removeEventListener('keydown', taste)
	}, [weiter, zurueck])

	// Nach dem Wechsel an den Anfang der Folie — im eingebetteten Browser wirkt
	// eine weiche Bewegung nicht, deshalb ohne.
	useEffect(() => {
		rahmenRef.current?.scrollIntoView({ block: 'start' })
	}, [index])

	// Den Stand mitschreiben, wenn jemand blättert. Entprellt, damit schnelles
	// Durchklicken nicht ein halbes Dutzend Aufrufe auslöst.
	useEffect(() => {
		if (laedt) return undefined
		sichere()
		return undefined
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [index])

	/* ------------------------------------------------------------ Handlungen */

	const antworte = (key, ergebnis) => {
		setAntworten(vorher => {
			const alt = vorher[key]
			// Einmal richtig bleibt richtig, und „auf Anhieb" lässt sich nicht
			// nachträglich erwerben.
			if (alt?.correct) return vorher
			return { ...vorher, [key]: { ...ergebnis, jokers: jokerBenutzt[key] || 0 } }
		})
		sichere()
	}

	const zieheJoker = key => {
		setJokerBenutzt(vorher => ({ ...vorher, [key]: (vorher[key] || 0) + 1 }))
		setAntworten(vorher =>
			vorher[key] ? { ...vorher, [key]: { ...vorher[key], jokers: (vorher[key].jokers || 0) + 1 } } : vorher
		)
		sichere()
	}

	/**
	 * Die Praxisaufgabe nachsehen.
	 *
	 * Geprüft wird am **eigenen Profil** und nicht an einem Häkchen: Die Aufgabe
	 * heißt „Profilbild hinterlegen", nicht „behaupten, eines hinterlegt zu
	 * haben". Nebenbei kommt das frisch geladene Profil in den Speicher — der
	 * die Abschlusskarte zeigt das neue Bild damit sofort.
	 */
	const sieheAufgabeNach = async aufgabe => {
		setPruefeAufgabe(true)
		try {
			const { data } = await http.get(TApi.COMMON_ME, { disableToast: true })
			props.logIn({ role: data.role, profile: data })

			/*
			 * Geprüft wird am **eigenen Profil** und nicht an einem Häkchen: Die
			 * Aufgabe heißt „Profilbild hinterlegen", nicht „behaupten, eines
			 * hinterlegt zu haben". Dass das frisch geladene Profil dabei in den
			 * Speicher kommt, ist der zweite Zweck — Karte, Abschlusskarte und
			 * Navigationsleiste zeigen das neue Bild damit sofort.
			 */
			if (data?.avatarHashName) {
				setAufgaben(vorher => (vorher.includes(aufgabe.key) ? vorher : [...vorher, aufgabe.key]))
				sichere(true)
			}
		} catch (error) {
			toast.error(errorText(error, 'Das ließ sich gerade nicht nachsehen.'))
		} finally {
			setPruefeAufgabe(false)
		}
	}

	const schliesseAb = () => sichere(true, true)

	/* ----------------------------------------------------------------- Bild */

	if (laedt) return <div className={styles.loading}>Das Onboarding wird geladen …</div>

	const fortschritt = Math.round(((index + 1) / SLIDES.length) * 100)

	return (
		<div className={styles.page} ref={rahmenRef}>
			<div className={styles.topbar}>
				<Link to={TRoutes.HANDBOOK} className={styles.back}>
					← Handbuch
				</Link>
				<div className={styles.progressWrap}>
					<div className={styles.progress}>
						<span className={styles.progressBar} style={{ width: `${fortschritt}%` }} />
					</div>
					<span className={styles.progressText}>
						{index + 1} / {SLIDES.length} · {richtig} von {TOTAL_QUESTIONS} Fragen richtig
					</span>
				</div>
				<span className={styles.jokerCount}>
					Joker {jokerLeft}/{JOKER_BUDGET}
				</span>
			</div>

			<div className={styles.stage}>
				{istFrage ? (
					<Quiz
						frage={folie}
						ergebnis={antworten[folie.key]}
						jokerLeft={jokerLeft}
						onAnswer={ergebnis => antworte(folie.key, ergebnis)}
						onJoker={() => zieheJoker(folie.key)}
					/>
				) : folie.kind === 'outro' ? (
					<div className={clsx(styles.slide, styles.outro)}>
						<div>
							{/*
							 * Zwei Fassungen: davor lädt die Folie zum Abschließen
							 * ein, danach gratuliert sie. Ein Schlussbild, das nach
							 * dem Bestehen dasselbe sagt wie davor, fühlt sich an,
							 * als wäre nichts passiert.
							 */}
							<h2 className={styles.bigTitle}>
								{stand?.completedAt ? folie.titleDone || folie.title : folie.title}
							</h2>
							<p className={styles.lead}>
								{stand?.completedAt ? folie.leadDone || folie.lead : folie.lead}
							</p>
							{!durch && (
								<p className={styles.outroOpen}>
									Es sind noch {TOTAL_QUESTIONS - beantwortet} Fragen offen. Zurückblättern lohnt sich
									— der Abschluss ist gleich erreicht.
								</p>
							)}
							{/*
							 * Nach dem Abschluss stand hier gar nichts mehr, und der
							 * einzige Weg an den Anfang war der erste der zwanzig
							 * Punkte in der Fußleiste. Jetzt steht der Weg zurück
							 * als Knopf da — und daneben der ins Handbuch, auf das
							 * der Text verweist.
							 */}
							<div className={styles.outroButtons}>
								{!stand?.completedAt && (
									<button type="button" className={styles.finishBtn} onClick={schliesseAb}>
										Onboarding abschließen
									</button>
								)}
								<button type="button" className={styles.restartBtn} onClick={() => setIndex(0)}>
									Von vorne ansehen
								</button>
								{stand?.completedAt && (
									<Link className={styles.outroLink} to={TRoutes.HANDBOOK}>
										Zum Handbuch
									</Link>
								)}
							</div>
						</div>
						<Licence stand={stand} />
					</div>
				) : folie.kind === 'compare' ? (
					<CompareSlide slide={folie} />
				) : (
					<Slide
						slide={folie}
						taskDone={folie.task ? aufgaben.includes(folie.task.key) : false}
						onTaskCheck={() => folie.task && sieheAufgabeNach(folie.task)}
						taskChecking={pruefeAufgabe}
					/>
				)}
			</div>

			<div className={styles.controls}>
				<button type="button" className={styles.navBtn} onClick={zurueck} disabled={index === 0}>
					Zurück
				</button>
				<div className={styles.dots}>
					{SLIDES.map((eintrag, i) => (
						<button
							key={eintrag.id || eintrag.key}
							type="button"
							aria-label={`Folie ${i + 1}`}
							title={eintrag.title || eintrag.question}
							className={clsx(
								styles.dotNav,
								i === index && styles.dotNavOn,
								!eintrag.kind && styles.dotNavQuiz,
								!eintrag.kind && antworten[eintrag.key]?.correct && styles.dotNavDone
							)}
							onClick={() => setIndex(i)}
						/>
					))}
				</div>
				<button
					type="button"
					className={clsx(styles.navBtn, styles.navBtnMain)}
					onClick={weiter}
					disabled={index === SLIDES.length - 1}
				>
					Weiter
				</button>
			</div>

			<p className={styles.credit}>
				Junior Datenbank · Entwicklung und Betrieb: <b>DaVinciLab</b>
			</p>
		</div>
	)
}

const mapDispatchToProps = dispatch => ({
	logIn: data => dispatch(logIn(data))
})

export default connect(null, mapDispatchToProps)(Training)
